/**
 * Voice Log Verification Component
 *
 * Displays parsed voice data for user verification
 * Allows editing before committing to workout log
 * Intensity displayed as 1-5 stars
 */

import { useState } from 'react';
import './VoiceLogVerification.css';

interface ParsedSet {
  exercise: string;
  reps: number;
  weight?: number;
  intensity?: number;
}

interface VoiceLogVerificationProps {
  data: ParsedSet;
  onConfirm: () => void;
  onEdit: (edited: ParsedSet) => void;
  onCancel: () => void;
}

export function VoiceLogVerification({
  data,
  onConfirm,
  onEdit,
  onCancel,
}: VoiceLogVerificationProps): JSX.Element {
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState(data);

  const handleEdit = (): void => {
    setIsEditing(true);
  };

  const handleSave = (): void => {
    onEdit(editedData);
  };

  const renderStars = (intensity: number): string => {
    return '⭐'.repeat(intensity);
  };

  const handleStarClick = (star: number): void => {
    const newData: ParsedSet = { ...editedData };
    if (star === editedData.intensity) {
      delete newData.intensity;
    } else {
      newData.intensity = star;
    }
    setEditedData(newData);
  };

  return (
    <div className="voice-verification-backdrop" onClick={onCancel}>
      <div className="voice-verification" onClick={(e) => e.stopPropagation()}>
        <div className="voice-verification__header">
          <div className="voice-verification__icon">🎤</div>
          <h3 className="voice-verification__title">I heard:</h3>
        </div>

        {!isEditing ? (
          <div className="voice-verification__preview">
            <div className="voice-verification__exercise">{data.exercise}</div>
            <div className="voice-verification__details">
              {data.weight && <span>{data.weight} lbs</span>}
              <span>{data.reps} reps</span>
              {data.intensity && <span>{renderStars(data.intensity)}</span>}
            </div>
          </div>
        ) : (
          <div className="voice-verification__edit">
            <input
              type="text"
              className="voice-verification__input"
              value={editedData.exercise}
              onChange={(e) => setEditedData({ ...editedData, exercise: e.target.value })}
              placeholder="Exercise"
            />
            <div className="voice-verification__row">
              <input
                type="number"
                className="voice-verification__input voice-verification__input--small"
                value={editedData.weight ?? ''}
                onChange={(e) => {
                  const newData: ParsedSet = { ...editedData };
                  if (e.target.value) {
                    newData.weight = parseFloat(e.target.value);
                  } else {
                    delete newData.weight;
                  }
                  setEditedData(newData);
                }}
                placeholder="Weight"
              />
              <input
                type="number"
                className="voice-verification__input voice-verification__input--small"
                value={editedData.reps}
                onChange={(e) =>
                  setEditedData({ ...editedData, reps: parseInt(e.target.value, 10) || 0 })
                }
                placeholder="Reps"
              />
            </div>
            <div className="voice-verification__intensity">
              <label className="voice-verification__label">Intensity (1-5)</label>
              <div className="voice-verification__star-rating">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    className={`voice-verification__star ${
                      editedData.intensity && editedData.intensity >= star
                        ? 'voice-verification__star--active'
                        : ''
                    }`}
                    onClick={() => handleStarClick(star)}
                  >
                    ⭐
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="voice-verification__actions">
          {!isEditing ? (
            <>
              <button
                type="button"
                className="voice-verification__button voice-verification__button--secondary"
                onClick={handleEdit}
              >
                Edit
              </button>
              <button
                type="button"
                className="voice-verification__button voice-verification__button--primary"
                onClick={onConfirm}
              >
                Confirm
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="voice-verification__button voice-verification__button--secondary"
                onClick={onCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="voice-verification__button voice-verification__button--primary"
                onClick={handleSave}
              >
                Save
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
